import type { InputHTMLAttributes, ReactNode, Ref } from 'react'
import { forwardRef } from 'react'
import { Field } from './Field'

export interface TextInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label?: ReactNode
  /** Label stays for screen readers, off the screen. */
  hideLabel?: boolean
  hint?: ReactNode
  error?: ReactNode
  value: string
  /** Gets the string, not the event. */
  onChange: (value: string) => void
  /** Render a textarea instead. For section reviews and notes. */
  multiline?: boolean
  /** Rows, when multiline. Default 3. */
  rows?: number
  /** Class for the wrapping field, not the input. */
  fieldClassName?: string
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    label,
    hideLabel,
    hint,
    error,
    value,
    onChange,
    multiline = false,
    rows = 3,
    className,
    fieldClassName,
    ...rest
  },
  ref,
) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint} error={error} className={fieldClassName}>
      {({ id, describedBy, invalid }) =>
        multiline ? (
          <textarea
            // Same props, same styling; the element differs, the contract does not.
            {...(rest as unknown as InputHTMLAttributes<HTMLTextAreaElement>)}
            ref={ref as unknown as Ref<HTMLTextAreaElement>}
            id={id}
            rows={rows}
            className={`rs-input ${invalid ? 'rs-input--invalid' : ''} ${className ?? ''}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
          />
        ) : (
          <input
            {...rest}
            ref={ref}
            id={id}
            className={`rs-input ${invalid ? 'rs-input--invalid' : ''} ${className ?? ''}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
          />
        )
      }
    </Field>
  )
})
